import {
    CoverageIdEnum,
    CoverageSpecificationInterface,
    PolicyProductIdEnum,
    PurchasedProductInterface,
} from 'outshared-lib';
import { coverageInputData } from '../data';
import {
    CoverageControlConfigInputInterface,
    CoverageInputDataInputInterface,
    PurchasedDefinitiveInterface,
} from '../interfaces';

export class CoverageInput {
    public id: CoverageIdEnum;
    public coverageIds: CoverageIdEnum[];
    public dependsOn?: CoverageIdEnum | CoverageIdEnum[];
    public price: number = 0;
    public discount: number = 0;
    public priceDiscount: number = 0;
    public acceptance?: boolean = false;
    public purchased: PurchasedDefinitiveInterface = { current: null, future: null };
    public data?: CoverageInputDataInputInterface;
    public productId: PolicyProductIdEnum;
    public featured?: boolean;

    constructor(input: CoverageControlConfigInputInterface, purchasedProduct: PurchasedProductInterface) {
        this.id = input.id;
        this.coverageIds = input.coverageIds;
        this.dependsOn = input.dependsOn || null;

        this.setCoverageInputData(input.id, purchasedProduct);
        this.setCoverage(input.id, purchasedProduct);
        this.setPremium(input.coverageIds, purchasedProduct);
        this.setFeatured(input);
    }

    public doNotAccept(): void {
        this.acceptance = false;
    }

    private setFeatured(input: CoverageControlConfigInputInterface): void {
        if (input?.featured) {
            this.featured = true;
        }
    }

    private setCoverage(coverageId: CoverageIdEnum, purchasedProduct: PurchasedProductInterface): void {
        const coverage = this.findCoverage(coverageId, purchasedProduct);
        this.setAcceptance(coverage);
        this.setPurchasedCoverageState(coverage, purchasedProduct);
    }

    private findCoverage(
        coverageId: CoverageIdEnum,
        purchasedProduct: PurchasedProductInterface,
    ): CoverageSpecificationInterface {
        const coverages = [
            ...purchasedProduct.coverage.baseCoverages,
            ...purchasedProduct.coverage.additionalCoverages,
        ];

        return coverages.find((coverage) => coverage.coverageId === coverageId);
    }

    private setCoverageInputData(coverageId: CoverageIdEnum, purchasedProduct: PurchasedProductInterface): void {
        const foundCoverageInputData = coverageInputData.find(
            (inputData) => inputData.productId === purchasedProduct.productId,
        );

        if (!foundCoverageInputData) {
            return;
        }

        this.data = foundCoverageInputData.inputs.find((_input) => _input.coverageId === coverageId);

        // WINS-11409
        if (!this.data && coverageId === CoverageIdEnum.PetCrem) {
            this.data = {
                coverageId: CoverageIdEnum.PetCrem,
                title: 'Crematie',
            };
        }
    }

    private setPremium(coverageIds: CoverageIdEnum[], purchasedProduct: PurchasedProductInterface): void {
        let price = 0;
        let discount = 0;
        let priceDiscount = 0;
        const coverages = [
            ...purchasedProduct.coverage.baseCoverages,
            ...purchasedProduct.coverage.additionalCoverages,
        ];
        const foundCoverages = coverages.filter((coverage) => coverageIds.includes(coverage.coverageId));

        if (foundCoverages.length < 1) {
            return;
        }

        foundCoverages.forEach((foundCoverage) => {
            price = foundCoverage?.premium?.premiumIncludingPremiumTax + price;
            discount = foundCoverage?.discount?.premiumIncludingPremiumTax + discount;
            priceDiscount = foundCoverage?.premiumIncludingDiscount?.premiumIncludingPremiumTax + priceDiscount;
        });

        this.price = price;
        this.discount = discount;
        this.priceDiscount = priceDiscount;
    }

    private setAcceptance(coverage: CoverageSpecificationInterface): void {
        if (!coverage) {
            return;
        }

        this.acceptance = coverage.acceptedIndication;
    }

    private setPurchasedCoverageState(
        coverage: CoverageSpecificationInterface,
        purchasedProduct: PurchasedProductInterface,
    ): void {
        if (!coverage || !this.hasCoverageDependencies(purchasedProduct)) {
            return;
        }

        if (coverage.purchasedCoverage) {
            this.purchased.current = {
                startDate: coverage.purchasedCoverage.startDate,
                endDate: coverage.purchasedCoverage.endDate,
                price: coverage.premium.premiumIncludingPremiumTax,
                discount: coverage.premiumIncludingDiscount.premiumIncludingPremiumTax,
                priceDiscount: coverage.premiumIncludingDiscount.premiumIncludingPremiumTax,
            };
        }

        if (coverage.purchasedCoverage && coverage.purchasedCoverageFuture) {
            this.purchase(
                coverage.purchasedCoverageFuture.startDate,
                coverage.premium.premiumIncludingPremiumTax,
                coverage.premiumIncludingDiscount.premiumIncludingPremiumTax,
                coverage.premiumIncludingDiscount.premiumIncludingPremiumTax,
            );
        }

        if (coverage.purchasedCoverage && !coverage.purchasedCoverage.endDate) {
            this.purchase(
                coverage.purchasedCoverage.startDate,
                coverage.premium.premiumIncludingPremiumTax,
                coverage.premiumIncludingDiscount.premiumIncludingPremiumTax,
                coverage.premiumIncludingDiscount.premiumIncludingPremiumTax,
            );
        }
    }

    private purchase(startDate: string, price: number = 0, discount: number = 0, priceDiscount: number = 0): void {
        this.purchased.future = {
            startDate: startDate,
            price: price,
            discount: discount,
            priceDiscount: priceDiscount,
        };
    }

    private hasCoverageDependencies(purchasedProduct: PurchasedProductInterface): boolean {
        if (!this.dependsOn) return true;

        let dependantCoverages: CoverageIdEnum[] = Array.isArray(this.dependsOn)
            ? this.dependsOn
            : ([this.dependsOn] as CoverageIdEnum[]);

        const purchasedDependantCoverages = [
            ...purchasedProduct.coverage.baseCoverages,
            ...purchasedProduct.coverage.additionalCoverages,
        ]
            .filter((depCoverage) => depCoverage.purchasedCoverage || depCoverage.purchasedCoverageFuture)
            .map((depCoverage) => depCoverage.coverageId);

        return dependantCoverages.every((coverage) => purchasedDependantCoverages.includes(coverage));
    }
}
